/*
 * onne-default-size.js — ÔNNE PDP variant availability per color (v2)
 * -----------------------------------------------------------------------------
 * PROBLEMA (raíz, reproducido 2026-06-29):
 *   El theme Horizon calcula `data-option-available` de las TALLAS de forma
 *   GLOBAL (¿existe esta talla en algún color?), NO para el color seleccionado.
 *   Y los COLORES sí se condicionan a la talla (asimetría: Color = opción 2).
 *   Consecuencias en productos con stock irregular (la mayoría):
 *     - Tallas agotadas para el color actual NO se marcan ni se tachan.
 *     - El usuario aterriza en combinaciones "Agotado" al cambiar de color.
 *     - Colores se desactivan aunque tengan stock en otra talla.
 *   La v1 confiaba en `data-option-available` (global) → no corregía nada salvo
 *   el caso raro de talla agotada en TODOS los colores.
 *
 * SOLUCIÓN (v2): usar la disponibilidad REAL por variante.
 *   1. Fuente de verdad: `/products/{handle}.js` → {variantId: available}.
 *      (Cada <input> de talla lleva `data-variant-id` = variante (colorActual+talla),
 *       así que la disponibilidad por color es directa e inmune a traducciones.)
 *   2. Al CARGAR y al CAMBIAR de color:
 *      a) Tacha (línea diagonal, .onne-size-oos) las tallas SIN stock del color.
 *      b) Auto-selecciona la primera talla CON stock (orden DOM = S→M→L) si la
 *         marcada está agotada para ese color → nunca caer en "Agotado".
 *      c) Reactiva todo color que tenga ≥1 talla con stock (colores siempre
 *         seleccionables; solo queda desactivado el color 100% agotado).
 *   3. NO toca el click directo del usuario sobre una talla (solo load + color).
 *
 * AISLADO + GRACEFUL: no modifica variant-picker.js (core Horizon). Todo va en
 * try/catch; si el fetch o el DOM fallan, cae al comportamiento nativo (no peor).
 * Cargado en blocks/variant-picker.liquid (<script type="module">).
 */
(function () {
  'use strict';
  if (window.__onneDefaultSizeV2) return;
  window.__onneDefaultSizeV2 = true;

  var COLOR_FIELDSET = '.variant-option--swatches';
  var SIZE_FIELDSET = '.variant-option--buttons:not(.variant-option--swatches)';
  var STYLE_ID = 'onne-size-oos-style';

  var availById = Object.create(null); // pid -> {variantId(str): bool}
  var colorStock = Object.create(null); // pid -> {colorValue(lower): bool any-size-in-stock}
  var fetching = Object.create(null);   // pid -> true mientras se descarga
  var lastColor = Object.create(null);  // pid -> color marcado en la última pasada

  function norm(x) { return (x == null ? '' : String(x)).trim().toLowerCase(); }

  // Tachado claro de la talla agotada para el color actual. Line-through sobre el
  // texto visible (.__text, z-index:1) — robusto en Horizon (el ::after del label
  // está display:none + lo tapa el pill). Más marcado que el nativo (fino al 40%).
  function injectCss() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.variant-option__button-label.onne-size-oos{opacity:.55}' +
      '.variant-option__button-label.onne-size-oos .variant-option__button-label__text{' +
      'text-decoration:line-through;text-decoration-thickness:2px;text-underline-offset:0}';
    (document.head || document.documentElement).appendChild(s);
  }

  function pidOf(picker, idx) {
    return picker.getAttribute('data-product-id') ||
           picker.getAttribute('data-section-id') || ('p' + (idx || 0));
  }

  function productUrl(picker) {
    var u = picker.getAttribute('data-product-url');
    if (u) return u.split('?')[0];
    var m = window.location.pathname.match(/^.*\/products\/[^/?#]+/);
    return m ? m[0] : null;
  }

  function labelFor(input) {
    return input.closest('label') ||
           (input.id ? document.querySelector('label[for="' + input.id + '"]') : null);
  }

  // Construye {variantId: available} y {colorValueLower: anyAvailable} desde product.js
  function buildMaps(picker, data, pid) {
    var byId = Object.create(null);
    (data.variants || []).forEach(function (v) { byId[String(v.id)] = !!v.available; });
    availById[pid] = byId;

    // colorStock: necesita saber qué valor de las options es el color.
    var colorFs = picker.querySelector(COLOR_FIELDSET);
    var sizeFs = picker.querySelector(SIZE_FIELDSET);
    var colorSet = Object.create(null), sizeSet = Object.create(null);
    if (colorFs) [].forEach.call(colorFs.querySelectorAll('input[type="radio"]'), function (i) { colorSet[norm(i.value)] = 1; });
    if (sizeFs) [].forEach.call(sizeFs.querySelectorAll('input[type="radio"]'), function (i) { sizeSet[norm(i.value)] = 1; });
    var cstock = Object.create(null);
    (data.variants || []).forEach(function (v) {
      var opts = v.options || [], cval = null;
      for (var i = 0; i < opts.length; i++) {
        var n = norm(opts[i]);
        if (colorSet[n]) { cval = n; break; }
      }
      if (cval == null) cval = '__nocolor__';
      if (v.available) cstock[cval] = true;
      else if (!(cval in cstock)) cstock[cval] = false;
    });
    colorStock[pid] = cstock;
  }

  function ensureMaps(picker, pid) {
    if (availById[pid] || fetching[pid]) return;
    var url = productUrl(picker);
    if (!url) return;
    fetching[pid] = true;
    fetch(url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (data) { if (data) buildMaps(picker, data, pid); })
      .catch(function () { /* graceful: sin mapa, no actuamos */ })
      .then(function () {
        fetching[pid] = false;
        try { applyPicker(picker, pid); } catch (e) {}
      });
  }

  // Disponibilidad REAL de una talla = available de su data-variant-id (=color actual+talla)
  function sizeAvailable(byId, input) {
    var vid = input.getAttribute('data-variant-id');
    if (vid != null && vid in byId) return byId[vid];
    return false; // sin id mapeable → trátala como agotada (conservador)
  }

  // Devuelve true si cambió la talla seleccionada.
  function applyPicker(picker, pid) {
    var byId = availById[pid];
    if (!byId) return false;
    var sizeFs = picker.querySelector(SIZE_FIELDSET);
    if (!sizeFs) return false; // producto sin opción de talla
    var colorFs = picker.querySelector(COLOR_FIELDSET);

    var selColorInput = colorFs ? colorFs.querySelector('input[type="radio"]:checked') : null;
    var curColor = selColorInput ? norm(selColorInput.value) : '__nocolor__';
    var firstTime = !(pid in lastColor);
    var colorChanged = !firstTime && lastColor[pid] !== curColor;

    // (a) Tachar tallas agotadas del color actual + localizar 1ª con stock + marcada
    var inputs = sizeFs.querySelectorAll('input[type="radio"]');
    var firstAvail = null, checked = null, checkedOk = false;
    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var ok = sizeAvailable(byId, inp);
      var lbl = labelFor(inp);
      if (lbl) lbl.classList.toggle('onne-size-oos', !ok);
      if (ok && !firstAvail) firstAvail = inp;
      if (inp.checked) { checked = inp; checkedOk = ok; }
    }

    // (c) Colores con stock → siempre seleccionables
    if (colorFs && colorStock[pid]) {
      var cstock = colorStock[pid];
      var cins = colorFs.querySelectorAll('input[type="radio"]');
      for (var c = 0; c < cins.length; c++) {
        var ci = cins[c];
        if (cstock[norm(ci.value)]) {
          ci.removeAttribute('aria-disabled');
          if (ci.disabled) ci.disabled = false;
          ci.setAttribute('data-option-available', 'true');
          var clab = labelFor(ci);
          if (clab) clab.classList.remove('onne-size-oos');
        }
      }
    }

    // (b) Auto-corregir la talla SOLO en carga o cambio de color (no en click de talla)
    var changed = false;
    if ((firstTime || colorChanged) && !checkedOk && firstAvail && firstAvail !== checked) {
      firstAvail.checked = true;
      firstAvail.dispatchEvent(new Event('change', { bubbles: true }));
      changed = true;
    }

    lastColor[pid] = curColor;
    return changed;
  }

  function processAll() {
    var pickers = document.querySelectorAll('variant-picker');
    for (var i = 0; i < pickers.length; i++) {
      var picker = pickers[i], pid = pidOf(picker, i);
      if (!availById[pid]) { ensureMaps(picker, pid); continue; }
      try { applyPicker(picker, pid); } catch (e) {}
    }
  }

  function init() {
    try { injectCss(); } catch (e) {}
    try { processAll(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Tras cada re-render del picker (cambio de color o talla) reevaluar. Dos ticks
  // (0 y 140ms) para cubrir el morph del DOM en móvil (más lento).
  document.addEventListener('variant:update', function () {
    setTimeout(processAll, 0);
    setTimeout(processAll, 140);
  });
})();
